import { useState, useEffect } from "react";
import { useAuth } from "../useAuth";
import { T } from "../lib/i18n";
import { dbLoadUserCourseList } from "../lib/db";
import { PageHead } from "../components/common";

// ─────────────────────────────────────────
//  COURSES SCREEN — "Mina kurser" in sidebar
// ─────────────────────────────────────────
function CoursesScreen({ onCourseView, onBack, lang, G, css }) {
  const { user } = useAuth();
  const [courseInstances, setCourseInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = T[lang];
  const sv = lang === "sv";

  useEffect(() => {
    if (!user) return;
    // Kurser, inte kurstillfällen: terminer hanteras automatiskt och behöver
    // inte väljas eller administreras.
    dbLoadUserCourseList(user.id).then(cs => { setCourseInstances(cs); setLoading(false); });
  }, [user]);

  // Gruppera per program, så en lärare med kurser i flera program ser strukturen
  const grouped = {};
  for (const c of courseInstances) {
    const key = c.programs?.name || "__other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  }
  const yearKeys = Object.keys(grouped).filter(k => k !== "__other").sort((a, b) => a.localeCompare(b));
  if (grouped["__other"]) yearKeys.push("__other");

  // Colour accent per course — stable hash from course id
  const ACCENTS = ["#60a5fa", "#34d399", "#a78bfa", "#f97316", "#f59e0b", "#ec4899"];
  function accentFor(id) {
    let h = 0;
    for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return ACCENTS[h % ACCENTS.length];
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto", padding: "36px 20px" }}>

      <PageHead
        G={G}
        title={sv ? "Mina kurser" : "My courses"}
        sub={sv ? "Välj en kurs för att se och hantera dess tRAT:er." : "Select a course to view and manage its tRATs."}
      />

      {/* Loading */}
      {loading && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", color: G.muted, fontSize: 14 }}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1s linear infinite" }}>
            <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" strokeDasharray="28" strokeDashoffset="10"/>
          </svg>
          {t.loading}
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      )}

      {/* Empty state */}
      {!loading && courseInstances.length === 0 && (
        <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: "52px 32px", textAlign: "center", maxWidth: 500, margin: "0 auto" }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: G.surface2, border: `1px solid ${G.border}`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <rect x="2" y="3" width="18" height="16" rx="3" stroke={G.muted} strokeWidth="1.3"/>
              <path d="M7 8h8M7 12h5" stroke={G.muted} strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
          </div>
          <p style={{ ...css.heading, fontSize: 16, color: G.text, marginBottom: 8 }}>
            {sv ? "Inga kurser tilldelade" : "No courses assigned"}
          </p>
          <p style={{ color: G.muted, fontSize: 14, lineHeight: 1.6 }}>
            {sv
              ? "Kontakta din kursadministratör för att bli tillagd i en kurs."
              : "Contact your course administrator to be added to a course."}
          </p>
        </div>
      )}

      {/* Year-grouped list */}
      {!loading && yearKeys.map(yearKey => (
        <div key={yearKey} style={{ marginBottom: 36 }}>
          {/* Year header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", color: G.muted, textTransform: "uppercase" }}>
              {yearKey === "__other" ? (sv ? "Övrigt" : "Other") : yearKey}
            </span>
            <div style={{ flex: 1, height: 1, background: G.border }} />
          </div>

          {/* Course cards in this year */}
          <div style={{ display: "grid", gap: 8 }}>
            {grouped[yearKey].map(course => {
              const program = course?.programs;
              const org = program?.organisations;
              const accent = accentFor(course?.id);
              const breadcrumb = [org?.name, program?.name].filter(Boolean);

              return (
                <div
                  key={course.id}
                  onClick={() => onCourseView(course)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = G.surface2; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.background = G.surface; }}
                  style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 16, transition: "border-color 0.15s, background 0.15s" }}
                >
                  {/* Accent stripe */}
                  <div style={{ width: 3, height: 36, borderRadius: 2, background: accent, flexShrink: 0 }} />

                  {/* Text block */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {/* Course name */}
                    <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: 15, color: G.text, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {course?.name || (sv ? "Okänd kurs" : "Unknown course")}
                    </div>
                    {/* Breadcrumb */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {breadcrumb.length > 0 && (
                        <span style={{ fontSize: 12, color: G.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {breadcrumb.join(" › ")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: G.muted }}>
                    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export { CoursesScreen };
